import { Badge, Box, Button, Divider, Group, Paper, Stack, Text } from '@mantine/core'
import { Edit2 } from 'lucide-react'
import { QuestionAnswer, QuestionSet } from '../../api/questions'
import { RichText } from '../common/RichText'
import { getImageSrc } from '../../utils/imageData'
import { stripLeadingQuestionNumber } from '../../utils/questionText'

interface QuestionSetComponentProps {
  initialValue: QuestionSet
  onEdit?: () => void
}

function ChoiceValueDisplay({ value }: { value: string }) {
  const imageSrc = getImageSrc(value)

  if (imageSrc) {
    return (
      <Box>
        <img
          src={imageSrc}
          alt="Answer choice"
          style={{
            maxWidth: '100%',
            maxHeight: 240,
            display: 'block',
            borderRadius: 8,
            border: '1px solid var(--sat-border-soft)',
          }}
        />
      </Box>
    )
  }

  return <RichText size="sm">{value}</RichText>
}

function QuestionAnswerDetail({ qa, index }: { qa: QuestionAnswer; index: number }) {
  const questionImageSrc = getImageSrc(qa.question_image)
  const displayQuestion = stripLeadingQuestionNumber(qa.question)

  return (
    <Paper p="md" radius="md" style={{ border: '1px solid var(--sat-border-soft)' }}>
      <Group gap={8} mb={10}>
        <Badge size="sm" color="blue" variant="light" circle>{index + 1}</Badge>
        <Badge size="xs" color={qa.question_type === 'spr' ? 'grape' : 'gray'} variant="light">
          {qa.question_type === 'spr' ? 'Student-Produced Response' : 'Multiple Choice'}
        </Badge>
      </Group>

      <Box mb={12}>
        <RichText>{displayQuestion}</RichText>
      </Box>

      {questionImageSrc && (
        <Box mb={12} style={{ border: '1px solid var(--sat-border-soft)', borderRadius: 8, overflow: 'hidden', display: 'inline-block' }}>
          <img
            src={questionImageSrc}
            alt="Question image"
            style={{ maxWidth: 400, maxHeight: 300, display: 'block' }}
          />
        </Box>
      )}

      {qa.question_type !== 'spr' && qa.choices.length > 0 && (
        <Stack gap={6} mb={10}>
          {qa.choices
            .slice()
            .sort((a, b) => a.display_order - b.display_order)
            .map((choice) => (
              <Group
                key={choice.id}
                gap={10}
                p={8}
                style={{
                  borderRadius: 8,
                  background: choice.is_correct ? 'var(--sat-green-soft)' : 'transparent',
                  border: choice.is_correct ? '1px solid var(--sat-green-border-soft)' : '1px solid transparent',
                }}
              >
                <Text
                  size="xs"
                  fw={700}
                  c={choice.is_correct ? 'var(--sat-green)' : 'var(--sat-text-faint)'}
                  style={{ width: 20, flexShrink: 0 }}
                >
                  {choice.choice_label}
                </Text>
                <Box style={{ flex: 1 }}>
                  <ChoiceValueDisplay value={choice.choice_text} />
                </Box>
                {choice.is_correct && <Badge size="xs" color="green">Correct</Badge>}
              </Group>
            ))}
        </Stack>
      )}

      {qa.question_type === 'spr' && (
        <Paper p="sm" radius="sm" style={{ background: 'var(--sat-green-soft)', border: '1px solid var(--sat-green-border-soft)' }} mb={10}>
          <Group gap={8}>
            <Text size="xs" fw={600} c="var(--sat-green)">Correct Answer:</Text>
            <Text size="sm" fw={700} c="var(--sat-text)">{qa.correct_answer_text || '—'}</Text>
            {qa.answer_tolerance != null && (
              <Text size="xs" c="var(--sat-text-faint)">(± {qa.answer_tolerance})</Text>
            )}
          </Group>
        </Paper>
      )}

      {qa.explanation && (
        <Paper p="sm" radius="sm" style={{ background: 'var(--sat-surface-info)', border: '1px solid var(--sat-border-soft)' }}>
          <Text size="xs" fw={600} c="var(--sat-info-text)" mb={4}>EXPLANATION</Text>
          <RichText size="sm">{qa.explanation}</RichText>
        </Paper>
      )}
    </Paper>
  )
}

export function QuestionSetComponent({ initialValue, onEdit }: QuestionSetComponentProps) {
  const q = initialValue
  const displayPassage = stripLeadingQuestionNumber(q.question_text)

  return (
    <Stack gap={16}>
      {onEdit && (
        <Group justify="flex-end">
          <Button leftSection={<Edit2 size={14} />} color="blue" variant="light" size="xs" onClick={onEdit}>
            Edit
          </Button>
        </Group>
      )}

      {(q.source || q.module || q.sequence_in_source != null) && (
        <Paper p="sm" radius="md" style={{ background: 'var(--sat-surface-alt)', border: '1px solid var(--sat-border-soft)' }}>
          <Group gap={8}>
            {q.source && <Badge size="sm" color="gray" variant="light">{q.source}</Badge>}
            {q.module && <Badge size="sm" color="blue" variant="light">Module {q.module}</Badge>}
            {q.sequence_in_source != null && (
              <Badge size="sm" color="cyan" variant="light">Sequence {q.sequence_in_source}</Badge>
            )}
          </Group>
        </Paper>
      )}

      {displayPassage && (
        <Paper p="md" radius="md" style={{ background: 'var(--sat-surface-alt)', border: '1px solid var(--sat-border-soft)' }}>
          <Text size="xs" fw={600} c="var(--sat-text-faint)" mb={6}>PASSAGE / CONTEXT</Text>
          <RichText>{displayPassage}</RichText>
        </Paper>
      )}

      {q.question_images.length > 0 && (
        <Paper p="md" radius="md" style={{ border: '1px solid var(--sat-border-soft)' }}>
          <Text size="xs" fw={600} c="var(--sat-text-faint)" mb={8}>QUESTION SET IMAGES</Text>
          <Group gap={12} wrap="wrap">
            {q.question_images.map((img, index) => {
              const imageSrc = getImageSrc(img)
              if (!imageSrc) return null

              return (
                <Box key={index} style={{ border: '1px solid var(--sat-border-soft)', borderRadius: 8, overflow: 'hidden' }}>
                  <img
                    src={imageSrc}
                    alt={`Question image ${index + 1}`}
                    style={{ maxWidth: 500, maxHeight: 400, display: 'block' }}
                  />
                </Box>
              )
            })}
          </Group>
        </Paper>
      )}

      {q.explanation && (
        <Paper p="sm" radius="md" style={{ background: 'var(--sat-surface-info)', border: '1px solid var(--sat-border-soft)' }}>
          <Text size="xs" fw={600} c="var(--sat-info-text)" mb={4}>SET EXPLANATION</Text>
          <RichText size="sm">{q.explanation}</RichText>
        </Paper>
      )}

      <Divider label={`${q.question_answers.length} Question${q.question_answers.length !== 1 ? 's' : ''}`} labelPosition="left" />

      {q.question_answers.map((qa, index) => (
        <QuestionAnswerDetail key={qa.id} qa={qa} index={index} />
      ))}

      {q.question_answers.length === 0 && (
        <Text c="var(--sat-text-faint)" size="sm" ta="center" py={20}>
          No questions in this set.
        </Text>
      )}
    </Stack>
  )
}
